import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { okrService, keyResultService, departamentoService, empresaService, okrAtividadeService } from '@/services/api';
import { OKR, KeyResult, Departamento, Empresa, OKRStatus, OKRAtividade } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Target, ChevronDown, ChevronUp, History, Building2, 
  PlusCircle, RefreshCw, TrendingUp, XCircle, MessageSquare, Edit3, FileText, Calendar
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getProgressIndicatorStyle } from '@/lib/progress-utils';
import { Navigate } from 'react-router-dom';
import { formatDateOnly } from '@/lib/dateOnly';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const statusLabels: Record<OKRStatus, string> = {
  a_fazer: 'A Fazer',
  fazendo: 'Fazendo',
  feito: 'Feito',
  pausado: 'Pausado',
  cancelado: 'Cancelado',
};

const statusStyles: Record<OKRStatus, string> = {
  a_fazer: 'status-todo',
  fazendo: 'status-doing',
  feito: 'status-done',
  pausado: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  cancelado: 'bg-red-100 text-red-800',
};

export default function GestaoOKRs() {
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  // Empresa filtrada vem do seletor global do header: pro superadmin é o
  // `empresaFiltroId` (pode ser "todas as empresas"); pra admin/comum é a empresa vinculada.
  const selectedEmpresa = (user?.tipo === 'superadmin' ? empresaFiltroId : empresaAtiva?.id) ?? 'all';
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [keyResults, setKeyResults] = useState<KeyResult[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedOKR, setExpandedOKR] = useState<string | null>(null);

  // Filter states
  const [filterDepartamento, setFilterDepartamento] = useState<string>('all');
  const [filterDataInicio, setFilterDataInicio] = useState<string>('');
  const [filterDataFim, setFilterDataFim] = useState<string>('');
  // Janela de anos aceitável para datas de OKR (ano atual -1 a +10) — mantida em sincronia com backend/src/utils/dateValidation.ts
  const anoAtual = new Date().getFullYear();
  const dataMin = `${anoAtual - 1}-01-01`;
  const dataMax = `${anoAtual + 10}-12-31`;
  // min/max do <input type="date"> só bloqueiam a escolha no calendário, não o valor digitado direto — por isso
  // esta função reforça a checagem no onChange (comparação de string funciona pois o formato é sempre AAAA-MM-DD).
  const handleFilterDateChange = (value: string, setter: (v: string) => void) => {
    if (value && (value < dataMin || value > dataMax)) {
      toast.error(`Ano fora do intervalo permitido (${anoAtual - 1}–${anoAtual + 10})`);
      return;
    }
    setter(value);
  };

  // Historico dialog state
  const [historicoDialogOpen, setHistoricoDialogOpen] = useState(false);
  const [historicoOkr, setHistoricoOkr] = useState<OKR | null>(null);
  const [atividades, setAtividades] = useState<OKRAtividade[]>([]);
  const [loadingAtividades, setLoadingAtividades] = useState(false);

  const isSuperAdmin = user?.tipo === 'superadmin';

  const hasActiveFilters = filterDepartamento !== 'all' || filterDataInicio || filterDataFim;

  const clearFilters = () => {
    setFilterDepartamento('all');
    setFilterDataInicio('');
    setFilterDataFim('');
  };

  // Reset filters when company changes
  useEffect(() => {
    clearFilters();
  }, [selectedEmpresa]);

  const loadEmpresas = async () => {
    const result = await empresaService.getAll();
    if (result.success) {
      setEmpresas(result.data.filter(e => e.status === 'ativa'));
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [okrsRes, krsRes, depsRes] = await Promise.all([
        okrService.getAll(),
        keyResultService.getAll(),
        departamentoService.getAll(),
      ]);

      if (okrsRes.success) setOkrs(okrsRes.data);
      if (krsRes.success) setKeyResults(krsRes.data);
      if (depsRes.success) setDepartamentos(depsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadEmpresas();
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  // Get departamentos for selected company
  const departamentosForEmpresa = selectedEmpresa === 'all'
    ? departamentos
    : departamentos.filter(d => d.empresa_id === selectedEmpresa);

  // Apply all filters
  const filteredOkrs = okrs.filter(okr => {
    // Company filter
    if (selectedEmpresa !== 'all' && okr.empresa_id !== selectedEmpresa) {
      return false;
    }
    // Department filter
    if (filterDepartamento !== 'all' && okr.departamento_id !== filterDepartamento) {
      return false;
    }
    // Date filters
    if (filterDataInicio && okr.data_inicio && okr.data_inicio < filterDataInicio) {
      return false;
    }
    if (filterDataFim && okr.data_fim && okr.data_fim > filterDataFim) {
      return false;
    }
    return true;
  });

  const getKRsForOKR = (okrId: string) => keyResults.filter((kr) => kr.okr_id === okrId).sort((a, b) => a.ordem - b.ordem);
  const getDepartamento = (depId: string) => departamentos.find((d) => d.id === depId);
  const getEmpresa = (empId: string) => empresas.find((e) => e.id === empId);

  const getPriorityLabel = (index: number) => {
    if (index === 0) return { label: 'Alta', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
    if (index === 1) return { label: 'Média', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' };
    if (index === 2) return { label: 'Normal', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
    return null;
  };

  const handleOpenHistorico = async (okr: OKR) => {
    setHistoricoOkr(okr);
    setHistoricoDialogOpen(true);
    setLoadingAtividades(true);
    
    const result = await okrAtividadeService.getByOkr(okr.id);
    if (result.success) {
      setAtividades(result.data);
    }
    setLoadingAtividades(false);
  };

  const getAtividadeIcon = (tipo: OKRAtividade['tipo']) => {
    switch (tipo) {
      case 'criacao': return PlusCircle;
      case 'atualizacao_status': return RefreshCw;
      case 'atualizacao_key_result': return TrendingUp;
      case 'cancelamento': return XCircle;
      case 'retorno_admin': return MessageSquare;
      case 'edicao': return Edit3;
      default: return FileText;
    }
  };

  const getAtividadeLabel = (tipo: OKRAtividade['tipo']) => {
    switch (tipo) {
      case 'criacao': return 'Criação';
      case 'atualizacao_status': return 'Status';
      case 'atualizacao_key_result': return 'Key Result';
      case 'cancelamento': return 'Cancelamento';
      case 'retorno_admin': return 'Retorno Admin';
      case 'edicao': return 'Edição';
      default: return 'Atividade';
    }
  };

  const getAtividadeBadgeClass = (tipo: OKRAtividade['tipo']) => {
    switch (tipo) {
      case 'criacao': return 'bg-green-100 text-green-800';
      case 'atualizacao_status': return 'bg-blue-100 text-blue-800';
      case 'atualizacao_key_result': return 'bg-purple-100 text-purple-800';
      case 'cancelamento': return 'bg-red-100 text-red-800';
      case 'retorno_admin': return 'bg-amber-100 text-amber-800';
      case 'edicao': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Only superadmin can access this page
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Gestão de OKRs</h1>
            <p className="text-muted-foreground">
              Visualize OKRs de todas as empresas
            </p>
          </div>
        </div>

        {selectedEmpresa !== 'all' && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">{getEmpresa(selectedEmpresa)?.nome}</p>
                  <p className="text-sm text-muted-foreground">
                    {filteredOkrs.length} OKR{filteredOkrs.length !== 1 ? 's' : ''} encontrada{filteredOkrs.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters - show when company is selected */}
        {selectedEmpresa !== 'all' && (
          <Card className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[150px] max-w-[200px]">
                <Label className="text-xs text-muted-foreground">Departamento</Label>
                <Select value={filterDepartamento} onValueChange={setFilterDepartamento}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {departamentosForEmpresa.map(dep => (
                      <SelectItem key={dep.id} value={dep.id}>
                        {dep.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 min-w-[140px] max-w-[180px]">
                <Label className="text-xs text-muted-foreground">Data Início</Label>
                <Input
                  type="date"
                  min={dataMin}
                  max={dataMax}
                  value={filterDataInicio}
                  onChange={(e) => handleFilterDateChange(e.target.value, setFilterDataInicio)}
                  className="h-9"
                />
              </div>

              <div className="flex-1 min-w-[140px] max-w-[180px]">
                <Label className="text-xs text-muted-foreground">Data Fim</Label>
                <Input
                  type="date"
                  min={dataMin}
                  max={dataMax}
                  value={filterDataFim}
                  onChange={(e) => handleFilterDateChange(e.target.value, setFilterDataFim)}
                  className="h-9"
                />
              </div>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9">
                  Limpar
                </Button>
              )}
            </div>
          </Card>
        )}

        <div className="space-y-4">
          {filteredOkrs.map((okr) => {
            const krs = getKRsForOKR(okr.id);
            const dep = getDepartamento(okr.departamento_id);
            const empresa = getEmpresa(okr.empresa_id);
            const isExpanded = expandedOKR === okr.id;

            return (
              <Card key={okr.id} className="card-interactive overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary flex-shrink-0" />
                      <CardTitle className="text-base md:text-lg line-clamp-2">{okr.titulo}</CardTitle>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge className={statusStyles[okr.status]}>
                        {statusLabels[okr.status]}
                      </Badge>
                      {dep && <Badge variant="outline">{dep.nome}</Badge>}
                      {selectedEmpresa === 'all' && empresa && (
                        <Badge variant="secondary" className="gap-1">
                          <Building2 className="h-3 w-3" />
                          {empresa.nome}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {okr.descricao}
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Progresso</span>
                        <span className="font-medium">{okr.progresso_geral}%</span>
                      </div>
                      <Progress value={okr.progresso_geral} className={cn("h-2", getProgressIndicatorStyle(okr.progresso_geral))} />
                    </div>
                    <TooltipProvider delayDuration={500}>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenHistorico(okr)}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Ver histórico</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setExpandedOKR(isExpanded ? null : okr.id)}
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{isExpanded ? 'Ocultar Key Results' : 'Ver Key Results'}</TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  </div>

                  {isExpanded && (
                    <div className="pt-4 border-t border-border space-y-3 animate-fade-in">
                      <h4 className="font-medium text-sm">Key Results ({krs.length})</h4>

                      {krs.map((kr, index) => {
                        const priority = getPriorityLabel(index);
                        return (
                          <div
                            key={kr.id}
                            className="p-3 rounded-lg bg-muted/50 space-y-2"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {priority && (
                                    <Badge className={cn("text-xs", priority.className)}>
                                      {priority.label}
                                    </Badge>
                                  )}
                                  {kr.prazo && (
                                    <Badge variant="outline" className="text-xs gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {formatDateOnly(kr.prazo)}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm">{kr.descricao}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <Progress value={kr.percentual} className={cn("flex-1 h-1.5", getProgressIndicatorStyle(kr.percentual))} />
                              <span className="text-xs text-muted-foreground w-24 text-right">
                                {kr.progresso_atual} / {kr.progresso_meta} ({kr.percentual}%)
                              </span>
                            </div>
                          </div>
                        );
                      })}

                      {krs.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Nenhum Key Result cadastrado
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {filteredOkrs.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Target className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {selectedEmpresa === 'all' 
                    ? 'Nenhuma OKR cadastrada no sistema' 
                    : 'Nenhuma OKR encontrada para esta empresa'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Historico Dialog */}
      <Dialog open={historicoDialogOpen} onOpenChange={(open) => { 
        if (!open) { 
          setHistoricoDialogOpen(false); 
          setHistoricoOkr(null);
          setAtividades([]);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Histórico de Atividades
            </DialogTitle>
            <DialogDescription>
              {historicoOkr?.titulo}
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[60vh] overflow-y-auto pr-4">
            {loadingAtividades ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : atividades.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma atividade registrada
              </div>
            ) : (
              <div className="space-y-4">
                {atividades.map((atividade) => {
                  const Icon = getAtividadeIcon(atividade.tipo);
                  return (
                    <div key={atividade.id} className="flex gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="p-2 rounded-full bg-background">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getAtividadeBadgeClass(atividade.tipo)}>
                            {getAtividadeLabel(atividade.tipo)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(atividade.data).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <p className="text-sm font-medium">{atividade.descricao}</p>
                        {atividade.detalhes && (
                          <p className="text-sm text-muted-foreground mt-1">{atividade.detalhes}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Por: {atividade.usuario_nome}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}